import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Loader, Text } from "@pingux/astro";
import ContentCopyIcon from "@pingux/mdi-react/ContentCopyIcon";
import CheckIcon from "@pingux/mdi-react/CheckIcon";
import axios from "axios";
import QRCode from "qrcode";
import {
  Credential,
  CredentialTotpCode,
  CredentialTotpConfig,
  TotpAlgorithm,
} from "@app-types/tap-partner-vault";
import { credentialsService } from "../../services/credentials";
import { decodeQrImageFile, parseOtpauthUri } from "./totpUtils";

interface CredentialTotpSectionProps {
  credential: Credential;
  isAdminUser: boolean;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

const detailCardStyle = {
  padding: "1.5rem",
  backgroundColor: "white",
  borderRadius: "0.5rem",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
  marginBottom: "1.5rem",
};

const labelStyle = {
  color: "#6b7280",
  fontSize: "0.75rem",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  marginBottom: "0.25rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.7rem 0.85rem",
  borderRadius: "0.5rem",
  border: "1px solid #d1d5db",
  fontSize: "0.95rem",
  color: "#111827",
  boxSizing: "border-box",
};

const fieldGroupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
};

const algorithmOptions: TotpAlgorithm[] = ["SHA1", "SHA256", "SHA512"];
const digitOptions = [6, 7, 8];

const CredentialTotpSection: React.FC<CredentialTotpSectionProps> = ({
  credential,
  isAdminUser,
}) => {
  const [config, setConfig] = useState<CredentialTotpConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [code, setCode] = useState<CredentialTotpCode | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualIssuer, setManualIssuer] = useState("");
  const [manualAccountLabel, setManualAccountLabel] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [manualAlgorithm, setManualAlgorithm] = useState<TotpAlgorithm>("SHA1");
  const [manualDigits, setManualDigits] = useState(6);
  const [manualPeriodSeconds, setManualPeriodSeconds] = useState(30);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const refreshingCodeRef = useRef(false);

  const shouldRender = isAdminUser && credential.credential_type === "admin_account";

  const loadConfig = useCallback(async () => {
    if (!shouldRender) return;

    try {
      setConfigLoading(true);
      setError(null);
      const nextConfig = await credentialsService.getTotp(credential.id);
      setConfig(nextConfig);
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 404) {
        setConfig(null);
      } else {
        setError(getErrorMessage(err, "Failed to load synced TOTP."));
      }
    } finally {
      setConfigLoading(false);
    }
  }, [credential.id, shouldRender]);

  const refreshCode = useCallback(async (showLoader = false) => {
    if (!config || refreshingCodeRef.current) return;

    refreshingCodeRef.current = true;
    try {
      if (showLoader) {
        setCode(null);
      }

      const nextCode = await credentialsService.getTotpCode(credential.id);
      setCode(nextCode);
      setExpiresAt(Date.now() + nextCode.expires_in_seconds * 1000);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load the current TOTP code."));
    } finally {
      refreshingCodeRef.current = false;
    }
  }, [config, credential.id]);

  useEffect(() => {
    if (!shouldRender) return;
    loadConfig();
  }, [loadConfig, shouldRender]);

  useEffect(() => {
    if (!config?.otpauth_uri) {
      setQrDataUrl(null);
      return;
    }

    let cancelled = false;
    Promise.resolve(QRCode.toDataURL(config.otpauth_uri, { width: 220, margin: 1 }))
      .then((dataUrl) => {
        if (!cancelled) {
          setQrDataUrl(dataUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrDataUrl(null);
          setError("Failed to render the enrollment QR code.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [config?.otpauth_uri]);

  useEffect(() => {
    if (!config) {
      setCode(null);
      setExpiresAt(null);
      return;
    }

    setNow(Date.now());
    refreshCode(true);

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [config, refreshCode]);

  useEffect(() => {
    if (!config || !expiresAt) return;
    if (expiresAt <= now + 250) {
      refreshCode();
    }
  }, [config, expiresAt, now, refreshCode]);

  const secondsLeft = useMemo(() => {
    if (!expiresAt) return null;
    return Math.max(0, Math.ceil((expiresAt - now) / 1000));
  }, [expiresAt, now]);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCopy = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  const handleSelectQr = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const openManualForm = useCallback(() => {
    setError(null);
    if (config) {
      setManualIssuer(config.issuer);
      setManualAccountLabel(config.account_label);
      setManualAlgorithm(config.algorithm);
      setManualDigits(config.digits);
      setManualPeriodSeconds(config.period_seconds);
    } else {
      setManualAlgorithm("SHA1");
      setManualDigits(6);
      setManualPeriodSeconds(30);
    }
    setManualSecret("");
    setShowManualForm(true);
  }, [config]);

  const closeManualForm = useCallback(() => {
    setShowManualForm(false);
    setManualSecret("");
  }, []);

  const handleQrFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      setError(null);
      const qrText = await decodeQrImageFile(file);
      const payload = parseOtpauthUri(qrText);
      await credentialsService.upsertTotp(credential.id, payload);
      await loadConfig();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to import the TOTP QR image."));
    } finally {
      setSaving(false);
      resetFileInput();
    }
  }, [credential.id, loadConfig]);

  const handleRemove = useCallback(async () => {
    if (!window.confirm("Remove the synced TOTP from this credential?")) {
      return;
    }

    try {
      setRemoving(true);
      setError(null);
      await credentialsService.deleteTotp(credential.id);
      setConfig(null);
      setCode(null);
      setExpiresAt(null);
      setQrDataUrl(null);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to remove synced TOTP."));
    } finally {
      setRemoving(false);
    }
  }, [credential.id]);

  const handleManualSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const issuer = manualIssuer.trim();
    const accountLabel = manualAccountLabel.trim();
    const secret = manualSecret.trim();

    if (!issuer || !accountLabel || !secret) {
      setError("Issuer, account label, and secret key are required.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await credentialsService.upsertTotp(credential.id, {
        issuer,
        account_label: accountLabel,
        secret,
        algorithm: manualAlgorithm,
        digits: manualDigits,
        period_seconds: manualPeriodSeconds,
      });
      await loadConfig();
      setShowManualForm(false);
      setManualSecret("");
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save the TOTP secret key."));
    } finally {
      setSaving(false);
    }
  }, [
    credential.id,
    loadConfig,
    manualAccountLabel,
    manualAlgorithm,
    manualDigits,
    manualIssuer,
    manualPeriodSeconds,
    manualSecret,
  ]);

  if (!shouldRender) {
    return null;
  }

  return (
    <Box sx={detailCardStyle}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleQrFile}
        style={{ display: "none" }}
      />

      <Text
        sx={{
          fontSize: "1rem",
          fontWeight: "600",
          color: "#111827",
          marginBottom: "0.75rem",
          paddingBottom: "0.5rem",
          borderBottom: "1px solid #e5e7eb",
          textAlign: "center",
        }}
      >
        Synced TOTP
      </Text>

      {error && (
        <Box
          sx={{
            padding: "0.75rem 1rem",
            backgroundColor: "#fee2e2",
            borderRadius: "0.5rem",
            marginBottom: "1rem",
            borderLeft: "4px solid #dc2626",
          }}
        >
          <Text sx={{ color: "#991b1b", fontSize: "0.875rem" }}>{error}</Text>
        </Box>
      )}

      {configLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
          <Loader />
        </Box>
      ) : !config ? (
        <Box sx={{ textAlign: "center" }}>
          <Text sx={{ color: "#4b5563", marginBottom: "0.75rem" }}>
            No synced TOTP has been attached to this credential yet.
          </Text>
          <Text sx={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "1rem" }}>
            Upload or capture a QR image from your authenticator enrollment flow to sync it with the vault.
          </Text>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
              marginBottom: showManualForm ? "1rem" : 0,
            }}
          >
            <Button variant="default" onPress={handleSelectQr} isLoading={saving}>
              Upload or Scan QR Image
            </Button>
            <Button variant="secondary" onPress={openManualForm} isDisabled={saving}>
              Enter Secret Key Manually
            </Button>
          </div>

          {showManualForm && (
            <form
              onSubmit={handleManualSubmit}
              style={{
                marginTop: "1.25rem",
                textAlign: "left",
                borderTop: "1px solid #e5e7eb",
                paddingTop: "1.25rem",
              }}
            >
              <Text sx={{ color: "#4b5563", fontSize: "0.875rem", marginBottom: "1rem" }}>
                Use manual entry when you have the raw TOTP secret key instead of an enrollment QR code.
              </Text>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "0.9rem",
                }}
              >
                <div style={fieldGroupStyle}>
                  <label htmlFor="totp-issuer" style={labelStyle}>Issuer</label>
                  <input
                    id="totp-issuer"
                    value={manualIssuer}
                    onChange={(event) => setManualIssuer(event.target.value)}
                    placeholder="Ping Identity"
                    style={inputStyle}
                  />
                </div>

                <div style={fieldGroupStyle}>
                  <label htmlFor="totp-account-label" style={labelStyle}>Account Label</label>
                  <input
                    id="totp-account-label"
                    value={manualAccountLabel}
                    onChange={(event) => setManualAccountLabel(event.target.value)}
                    placeholder="admin@example.com"
                    style={inputStyle}
                  />
                </div>

                <div style={{ ...fieldGroupStyle, gridColumn: "1 / -1" }}>
                  <label htmlFor="totp-secret-key" style={labelStyle}>Secret Key</label>
                  <input
                    id="totp-secret-key"
                    value={manualSecret}
                    onChange={(event) => setManualSecret(event.target.value)}
                    placeholder="JBSWY3DPEHPK3PXP"
                    autoComplete="off"
                    spellCheck={false}
                    style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: "0.05em" }}
                  />
                </div>

                <div style={fieldGroupStyle}>
                  <label htmlFor="totp-algorithm" style={labelStyle}>Algorithm</label>
                  <select
                    id="totp-algorithm"
                    value={manualAlgorithm}
                    onChange={(event) => setManualAlgorithm(event.target.value as TotpAlgorithm)}
                    style={inputStyle}
                  >
                    {algorithmOptions.map((algorithm) => (
                      <option key={algorithm} value={algorithm}>
                        {algorithm}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={fieldGroupStyle}>
                  <label htmlFor="totp-digits" style={labelStyle}>Digits</label>
                  <select
                    id="totp-digits"
                    value={manualDigits}
                    onChange={(event) => setManualDigits(Number(event.target.value))}
                    style={inputStyle}
                  >
                    {digitOptions.map((digits) => (
                      <option key={digits} value={digits}>
                        {digits}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={fieldGroupStyle}>
                  <label htmlFor="totp-period-seconds" style={labelStyle}>Period (seconds)</label>
                  <input
                    id="totp-period-seconds"
                    type="number"
                    min={15}
                    max={120}
                    value={manualPeriodSeconds}
                    onChange={(event) => setManualPeriodSeconds(Number(event.target.value))}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
                <Button type="submit" variant="default" isLoading={saving}>
                  Save Secret Key
                </Button>
                <Button type="button" variant="secondary" onPress={closeManualForm} isDisabled={saving}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </Box>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1.5rem",
              alignItems: "start",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Text sx={{ ...labelStyle, textAlign: "center" }}>Enrollment QR</Text>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Synced TOTP enrollment QR code"
                  style={{
                    width: "220px",
                    maxWidth: "100%",
                    borderRadius: "0.5rem",
                    border: "1px solid #e5e7eb",
                    padding: "0.5rem",
                    backgroundColor: "#fff",
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
                  <Loader />
                </Box>
              )}

              <a
                href={config.otpauth_uri}
                style={{
                  display: "inline-block",
                  marginTop: "0.75rem",
                  color: "#2563eb",
                  fontSize: "0.85rem",
                }}
              >
                Open in authenticator app
              </a>
            </Box>

            <Box>
              <div style={{ marginBottom: "1rem" }}>
                <Text sx={labelStyle}>Current Code</Text>
                {code ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <Text
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "2rem",
                        fontWeight: "700",
                        color: "#111827",
                        letterSpacing: "0.12em",
                      }}
                    >
                      {code.code}
                    </Text>
                    <Button
                      variant="link"
                      onPress={() => handleCopy(code.code)}
                      sx={{ color: copied ? "#059669" : "#6b7280", padding: "0" }}
                    >
                      {copied ? <CheckIcon size={16} /> : <ContentCopyIcon size={16} />}
                    </Button>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "0.25rem 0.55rem",
                        borderRadius: "9999px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        backgroundColor: secondsLeft !== null && secondsLeft <= 5 ? "#fee2e2" : "#dbeafe",
                        color: secondsLeft !== null && secondsLeft <= 5 ? "#b91c1c" : "#1d4ed8",
                      }}
                    >
                      Refreshes in {secondsLeft ?? "…"}s
                    </span>
                  </div>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Loader size="sm" />
                    <Text sx={{ color: "#6b7280" }}>Loading current code…</Text>
                  </Box>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <Text sx={labelStyle}>Issuer</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>{config.issuer}</Text>
                </div>
                <div>
                  <Text sx={labelStyle}>Account Label</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>{config.account_label}</Text>
                </div>
                <div>
                  <Text sx={labelStyle}>Algorithm</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>{config.algorithm}</Text>
                </div>
                <div>
                  <Text sx={labelStyle}>Digits</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>{config.digits}</Text>
                </div>
                <div>
                  <Text sx={labelStyle}>Period</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>{config.period_seconds}s</Text>
                </div>
                <div>
                  <Text sx={labelStyle}>Updated</Text>
                  <Text sx={{ color: "#111827", fontWeight: 600 }}>
                    {new Date(config.updated_at).toLocaleString()}
                  </Text>
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <Button variant="default" onPress={handleSelectQr} isLoading={saving}>
                  Replace QR Image
                </Button>
                <Button variant="secondary" onPress={openManualForm} isDisabled={saving}>
                  Replace with Secret Key
                </Button>
                <Button variant="critical" onPress={handleRemove} isLoading={removing}>
                  Remove Synced TOTP
                </Button>
              </div>

              {showManualForm && (
                <form
                  onSubmit={handleManualSubmit}
                  style={{
                    marginTop: "1.25rem",
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "1.25rem",
                  }}
                >
                  <Text sx={{ color: "#4b5563", fontSize: "0.875rem", marginBottom: "1rem" }}>
                    Replace the existing synced TOTP by entering a new secret key and its settings directly.
                  </Text>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "0.9rem",
                    }}
                  >
                    <div style={fieldGroupStyle}>
                      <label htmlFor="totp-replace-issuer" style={labelStyle}>Issuer</label>
                      <input
                        id="totp-replace-issuer"
                        value={manualIssuer}
                        onChange={(event) => setManualIssuer(event.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div style={fieldGroupStyle}>
                      <label htmlFor="totp-replace-account-label" style={labelStyle}>Account Label</label>
                      <input
                        id="totp-replace-account-label"
                        value={manualAccountLabel}
                        onChange={(event) => setManualAccountLabel(event.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ ...fieldGroupStyle, gridColumn: "1 / -1" }}>
                      <label htmlFor="totp-replace-secret-key" style={labelStyle}>Secret Key</label>
                      <input
                        id="totp-replace-secret-key"
                        value={manualSecret}
                        onChange={(event) => setManualSecret(event.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: "0.05em" }}
                      />
                    </div>

                    <div style={fieldGroupStyle}>
                      <label htmlFor="totp-replace-algorithm" style={labelStyle}>Algorithm</label>
                      <select
                        id="totp-replace-algorithm"
                        value={manualAlgorithm}
                        onChange={(event) => setManualAlgorithm(event.target.value as TotpAlgorithm)}
                        style={inputStyle}
                      >
                        {algorithmOptions.map((algorithm) => (
                          <option key={algorithm} value={algorithm}>
                            {algorithm}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={fieldGroupStyle}>
                      <label htmlFor="totp-replace-digits" style={labelStyle}>Digits</label>
                      <select
                        id="totp-replace-digits"
                        value={manualDigits}
                        onChange={(event) => setManualDigits(Number(event.target.value))}
                        style={inputStyle}
                      >
                        {digitOptions.map((digits) => (
                          <option key={digits} value={digits}>
                            {digits}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={fieldGroupStyle}>
                      <label htmlFor="totp-replace-period-seconds" style={labelStyle}>Period (seconds)</label>
                      <input
                        id="totp-replace-period-seconds"
                        type="number"
                        min={15}
                        max={120}
                        value={manualPeriodSeconds}
                        onChange={(event) => setManualPeriodSeconds(Number(event.target.value))}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
                    <Button type="submit" variant="default" isLoading={saving}>
                      Save Secret Key
                    </Button>
                    <Button type="button" variant="secondary" onPress={closeManualForm} isDisabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </Box>
          </div>
        </>
      )}
    </Box>
  );
};

export default CredentialTotpSection;
